import { useCallback, useEffect, useMemo, useState } from 'react';

import { IColumnMapping, TParsedData } from '../../../../types/addressBook';
import { ICsvColumn } from '../csvUploadProfile';

interface IProps {
  dataAddresses: TParsedData[] | null;
  columns: ICsvColumn[];
}

export const useColumnMapping = ({ dataAddresses, columns }: IProps) => {
  const [mappedColumns, setMappedColumns] = useState<IColumnMapping[]>([]);
  const [amountOfIssues, setAmountOfIssues] = useState(0);

  const userHeaders = useMemo(() => Object.keys(dataAddresses?.[0] || {}), [dataAddresses]);

  useEffect(() => {
    if (!dataAddresses?.length) {
      return;
    }

    const newMappedColumns = columns.map((column) => {
      const hasMatch = userHeaders.includes(column.title);

      return {
        title: column.title,
        mappedTo: hasMatch ? column.title : '',
        sampleData: column.sampleData,
        type: column.type,
        id: column.id,
        isOptional: column.isOptional,
      };
    });

    setMappedColumns(newMappedColumns);
  }, [dataAddresses, userHeaders, columns]);

  useEffect(() => {
    if (!mappedColumns.length) {
      return;
    }

    const amountOfEmptyColumns = mappedColumns.filter((column) => !column.mappedTo && !column.isOptional).length;

    const mappedCounts = new Map<string, number>();
    mappedColumns.forEach((col) => {
      if (col.mappedTo) {
        mappedCounts.set(col.mappedTo, (mappedCounts.get(col.mappedTo) || 0) + 1);
      }
    });
    const amountOfDuplicates = Array.from(mappedCounts.values()).filter((count) => count > 1).length;

    setAmountOfIssues(amountOfEmptyColumns + amountOfDuplicates);
  }, [mappedColumns]);

  const handleMapColumns = useCallback((id: string, value: string) => {
    setMappedColumns((prev) =>
      prev.map((col) => {
        if (col.id === id) {
          return { ...col, mappedTo: value };
        }
        return col;
      }),
    );
  }, []);

  const clearMappedColumns = useCallback(() => {
    setMappedColumns([]);
    setAmountOfIssues(0);
  }, []);

  return {
    mappedColumns,
    userHeaders,
    amountOfIssues,
    handleMapColumns,
    clearMappedColumns,
  };
};
